import { Anchor, Box, Button, Code, Drawer, Group, Stack, Text } from '@mantine/core';
import { EstatePill, KV, KVRow, KVSection, Pill } from '../../ui';
import { dateTime } from '../../ui/format';
import { tokens as T } from '../../theme/tokens';
import { useUIStore } from '../../stores/uiStore';
import { useMemoriesStore } from '../../stores/dataStores';
import { getSources } from '../../stores/sources';
import { useConsolePrefix } from '../../shell/nav';
import { canForget, parseScope, supersessionChain } from '../../data/memoryState';
import type { Memory } from '../../data/types';

/** The memory detail drawer. Everything it shows is a text node: the text,
 *  the tags, the scope id and the source ref were written by an agent, and
 *  nothing here renders them as markup or builds a URL from them. A hit
 *  from the recall debugger that is not on the loaded page can open here
 *  too — it is passed in, never inserted into the list store. */
export function MemoryDrawer({ extra }: { extra?: Record<string, Memory> }) {
  const selection = useUIStore((s) => s.memorySelection);
  const select = useUIStore((s) => s.selectMemory);
  const openModal = useUIStore((s) => s.openModal);
  const byId = useMemoriesStore((s) => s.data.byId);
  const harness = useConsolePrefix() === '/dev';
  const live = !harness && getSources().live.kind === 'live';
  const row = selection ? (byId[selection] ?? extra?.[selection]) : undefined;
  const chain = selection ? supersessionChain({ ...extra, ...byId }, selection) : undefined;

  return (
    <Drawer
      opened={Boolean(row)}
      onClose={() => select(null)}
      position="right"
      size={640}
      padding={0}
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.35, color: T.ink }}
      styles={{
        content: {
          background: T.card,
          borderLeft: `1px solid ${T.lineHard}`,
          borderRadius: '16px 0 0 16px',
          boxShadow: '-20px 0 50px rgba(46,42,38,0.26)',
          display: 'flex',
          flexDirection: 'column',
        },
        body: { flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', padding: 0 },
      }}
    >
      {row ? (
        <>
          <Group justify="space-between" align="flex-start" gap={14} px={22} py={18} style={{ background: T.rail, flex: '0 0 auto' }}>
            <Box style={{ minWidth: 0 }}>
              <Text fz={10} fw={700} c={T.ink4} style={{ letterSpacing: '0.09em' }}>
                MEMORY
              </Text>
              <Text ff="monospace" fz={15} fw={600} mt={5} style={{ wordBreak: 'break-all' }}>
                {row.id}
              </Text>
              <Group gap={8} mt={9} wrap="wrap">
                <EstatePill estate={row.estateId} size="md" />
                <Pill tone={row.status === 'active' ? 'ok' : row.status === 'deleted' ? 'fail' : 'neutral'} size="md">
                  {row.status.toUpperCase()}
                </Pill>
                <Pill tone="neutral" size="md">
                  {(row.kind || 'fact').toUpperCase()}
                </Pill>
                <Pill tone={row.indexed ? 'ok' : 'warn'} size="md">
                  {row.indexed ? 'INDEXED' : 'INDEX PENDING'}
                </Pill>
                <Text fz={12} c={T.ink3}>
                  v{row.version} · {dateTime(row.updatedAt)}
                </Text>
              </Group>
            </Box>
            <Group gap={12} wrap="nowrap" style={{ flex: '0 0 auto' }}>
              <Button
                variant="default"
                size="xs"
                color="red"
                disabled={!canForget(row, live, false)}
                title={
                  row.status !== 'active'
                    ? 'only an active memory can be forgotten'
                    : live
                      ? undefined
                      : 'not connected to a live memory store'
                }
                onClick={() =>
                  openModal({ kind: 'forgetMemory', estateId: row.estateId, tenantId: row.tenantId, id: row.id })
                }
              >
                Forget
              </Button>
              <Button variant="default" size="xs" onClick={() => select(null)}>
                Close
              </Button>
            </Group>
          </Group>

          <Box px={22} py={18} style={{ overflowY: 'auto', flex: '1 1 auto' }}>
            <Stack gap={18}>
              <KVSection title="Title">
                <Box p={13}>
                  <Text fz={14} fw={600} style={{ overflowWrap: 'anywhere' }} c={row.title ? undefined : T.ink4}>
                    {row.title || 'Untitled (written before titles were required)'}
                  </Text>
                </Box>
              </KVSection>

              <KVSection title="Text">
                <Box p={13}>
                  <Code block style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 360, overflowY: 'auto' }}>
                    {row.text}
                  </Code>
                </Box>
              </KVSection>

              <KVSection title="Where">
                <KVRow>
                  <KV k="Scope" v={scopeLabel(row.scope)} mono />
                </KVRow>
                <KVRow>
                  <KV k="Tenant" v={`${row.estateId} / ${row.tenantId}`} mono />
                </KVRow>
                <KVRow>
                  <KV k="Categories" v={row.categories?.length ? row.categories.join(' · ') : '—'} />
                </KVRow>
                <KVRow>
                  <KV k="Tags" v={row.tags?.length ? row.tags.join(', ') : '—'} mono />
                </KVRow>
              </KVSection>

              <KVSection title="Provenance">
                <KVRow>
                  <KV k="Asserted by" v={row.source.actor || 'agent'} />
                </KVRow>
                <KVRow>
                  <KV k="Source ref" v={row.source.ref || '—'} mono />
                </KVRow>
                <KVRow>
                  <KV k="Subject" v={row.source.subject || '—'} mono />
                </KVRow>
                <KVRow>
                  <KV k="Deployment" v={row.source.deploymentId || '—'} mono />
                </KVRow>
                <KVRow>
                  <KV k="Observed" v={dateTime(row.observedAt)} />
                </KVRow>
                <KVRow>
                  <KV k="Created" v={dateTime(row.createdAt)} />
                </KVRow>
                <KVRow>
                  <KV k="Updated" v={dateTime(row.updatedAt)} />
                </KVRow>
                {row.deletedAt ? (
                  <KVRow>
                    <KV k="Forgotten" v={dateTime(row.deletedAt)} />
                  </KVRow>
                ) : null}
              </KVSection>

              <KVSection title="Supersession">
                {chain && chain.ancestors.length === 0 && chain.descendants.length === 0 && chain.missing.length === 0 ? (
                  <Box p={13}>
                    <Text fz={12} c={T.ink4}>
                      This memory replaced nothing and has not been replaced.
                    </Text>
                  </Box>
                ) : null}
                {chain?.descendants.map((id) => (
                  <KVRow key={`d-${id}`}>
                    <KV k="Replaced by" v={<Anchor fz={12} ff="monospace" onClick={() => select(id)}>{id}</Anchor>} />
                  </KVRow>
                ))}
                {chain?.ancestors.map((id) => (
                  <KVRow key={`a-${id}`}>
                    <KV k="Replaced" v={<Anchor fz={12} ff="monospace" onClick={() => select(id)}>{id}</Anchor>} />
                  </KVRow>
                ))}
                {chain?.missing.map((id) => (
                  <KVRow key={`m-${id}`}>
                    <KV k="Not on this page" v={id} mono fg={T.ink4} />
                  </KVRow>
                ))}
              </KVSection>
            </Stack>
          </Box>
        </>
      ) : null}
    </Drawer>
  );
}

function scopeLabel(scope: string): string {
  const p = parseScope(scope);
  return p.kind === 'global' ? 'global (tenant-wide)' : scope;
}
